import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ChevronDown } from "lucide-react";
import { fmtCurrency, niceName } from "@/lib/format";
import { remindAction } from "@/components/RemindPreview";

const MON = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
export const card = "bg-white rounded-[18px] border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]";
const hello = () => { const h = new Date().getHours(); return h < 13 ? "Bom dia" : h < 20 ? "Boa tarde" : "Boa noite"; };
const dm = (iso) => { const [, m, d] = iso.slice(0, 10).split("-"); return `${Number(d)} ${MON[Number(m) - 1]}`; };
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// Só tarefas dos processos: chamadas pedidas pela gestão e o próximo passo de cada processo.
const buckets = (b) => ({
  hoje: [...b.calls.map((a) => ({ ...a, call: true })), ...b.tasks.filter((a) => a.bucket === "hoje")].filter((a) => a.pid),
  semana: b.tasks.filter((a) => a.pid && (!a.bucket || a.bucket === "semana")),
  espera: b.tasks.filter((a) => a.pid && a.bucket === "espera"),
});

export function dayLine(b) {
  const g = buckets(b);
  const n = g.hoje.length || g.semana.length;
  if (!n && !g.espera.length) return "nada urgente hoje";
  return g.hoje.length ? `${plural(g.hoje.length, "pendente", "pendentes")} para hoje` : n ? `${plural(n, "pendente", "pendentes")} esta semana` : `${g.espera.length} à espera do cliente`;
}

export function DayHeader({ d }) {
  const next = (d.upcoming_list || [])[0];
  return (
    <div className="navy-bg text-white rounded-[12px] px-4 py-2.5 md:px-5 flex flex-wrap items-baseline gap-x-1.5 gap-y-0" data-testid="money-strip">
      <span className="text-[15px] md:text-base font-bold" data-testid="greeting">{hello()}, {niceName(d.name)?.split(" ")[0]}</span>
      <span className="text-sm text-white/75" data-testid="todo-count">· {dayLine(d.board)}</span>
      {next && <Link to={`/processo/${next.id}`} className="text-sm text-white/75 hover:underline" data-testid="next-deed">· próxima escritura {dm(next.date)}</Link>}
    </div>
  );
}

const DOT = { hoje: "bg-red-500", semana: "bg-amber-400", espera: "bg-slate-300" };
const verb = (a) => a.verb || (a.call ? "Ligar" : a.kind === "remind" ? "Lembrar" : /assinar/i.test(`${a.button} ${a.title}`) && !/assinado/i.test(a.title) ? "Assinar" : "Ver");

function TodoRow({ a, bucket }) {
  const nav = useNavigate();
  const p = a.call ? "call" : a.prep ? "prep" : "task";
  const t = a.prep ? a.id.replace(/:/g, "-") : a.pid;
  const go = () => {
    if (a.call) return a.phone ? (window.location.href = `tel:${String(a.phone).replace(/\s/g, "")}`) : nav(`/processo/${a.pid}#sec-contactos`);
    return a.kind === "remind" ? remindAction(a) : nav(`/processo/${a.pid}${a.target || ""}`);
  };
  const v = verb(a);
  return (
    <li className="rounded-[12px] border border-slate-100 bg-white px-3.5 py-3 flex items-center gap-3 transition-shadow hover:shadow-md" data-testid={`${p}-${t}`}>
      <span className={`w-2 h-2 rounded-full shrink-0 self-start mt-2 ${a.call ? "bg-red-500" : DOT[bucket]}`} />
      <div className="flex-1 min-w-0">
        <Link to={`/processo/${a.pid}`} className="block text-[15px] font-semibold text-slate-900 leading-snug hover:underline" data-testid={`${p}-title-${t}`}>{a.title}</Link>
        <Link to={`/processo/${a.pid}`} className="block text-[13px] text-slate-500 truncate hover:underline" data-testid={`${p}-where-${t}`}>{[a.where, a.call ? null : a.sub].filter(Boolean).join(" · ")}</Link>
      </div>
      <button onClick={go} className={`h-10 px-3.5 rounded-[10px] text-sm font-semibold shrink-0 transition-colors ${v === "Lembrar" ? "border border-slate-300 bg-white navy-text hover:bg-slate-50" : "navy-bg text-white hover:opacity-90"}`} data-testid={`${p}-btn-${t}`}>{v}</button>
    </li>
  );
}

export function TodoCard({ b }) {
  const g = buckets(b);
  const none = !g.hoje.length && !g.semana.length && !g.espera.length;
  const part = (key, title, rows) => rows.length > 0 && (
    <div className="mt-3 first:mt-0" data-testid={`todo-${key}`}>
      <div className="text-[11px] font-bold tracking-[0.12em] text-slate-500 uppercase">{title} · {rows.length}</div>
      <ul className="mt-2 space-y-2">{rows.map((a) => <TodoRow key={a.id} a={a} bucket={key} />)}</ul>
    </div>
  );
  return (
    <section data-testid="todo-card" aria-label="O que fazer">
      <h2 className="sr-only">O que fazer</h2>
      {none ? <p className="text-[15px] font-semibold text-green-700 bg-green-50 rounded-[12px] px-4 py-3" data-testid="todo-nada-hoje">Nada urgente hoje</p> : (
        <>
          {part("hoje", "Hoje", g.hoje)}
          {part("semana", "Esta semana", g.semana)}
          {part("espera", "À espera do cliente", g.espera)}
        </>
      )}
    </section>
  );
}

function DealRow({ r, gp }) {
  const line = gp ? [r.who, r.when, r.status].filter(Boolean).join(" · ") : r.next ? `Próximo: ${r.next}` : r.sub;
  return (
    <Link to={`/processo/${r.id}`} className="block rounded-[12px] border border-slate-100 bg-white px-3.5 py-3 transition-shadow hover:shadow-md" data-testid={`deal-${r.ref}`}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[15px] font-semibold text-slate-900 truncate">{r.address}</span>
        {r.price ? <span className="text-sm font-semibold navy-text tabular-nums shrink-0">{fmtCurrency(r.price)}</span> : null}
      </div>
      <div className="text-[13px] text-slate-500 truncate" data-testid={`deal-${gp ? "gp" : "next"}-${r.ref}`}>{line}</div>
    </Link>
  );
}

export function DealsCard({ b, total }) {
  const rows = [...b.mine.map((r) => ({ r, gp: false })), ...b.gestao.map((r) => ({ r, gp: true }))];
  return (
    <section data-testid="deals-card">
      <div className="flex justify-between items-baseline">
        <h2 className="text-[11px] font-bold tracking-[0.12em] text-slate-500 uppercase">Os meus negócios · {rows.length}</h2>
        <Link to="/processos" className="text-sm text-slate-500 hover:underline inline-flex items-center gap-1" data-testid="deals-all">Ver todos · {total} <ArrowRight size={13} /></Link>
      </div>
      {!rows.length && <p className="text-sm text-slate-500 mt-2">Sem negócios em curso.</p>}
      <div className="mt-2 space-y-2">{rows.map(({ r, gp }) => <DealRow key={r.id} r={r} gp={gp} />)}</div>
    </section>
  );
}

export function NumbersBody({ d }) {
  const pct = d.goal ? Math.round((d.earned / d.goal) * 100) : null;
  const n = d.upcoming_n ?? 0;
  return (
    <div className="grid gap-4 sm:grid-cols-3" data-testid="numbers-body">
      <div>
        <div className="text-2xl font-bold navy-text tabular-nums" data-testid="earned">{fmtCurrency(d.earned)}</div>
        <div className="text-sm text-slate-500">já ganhos em {d.year}</div>
      </div>
      <div>
        <div className="text-2xl font-bold navy-text tabular-nums" data-testid="goal">{d.goal ? fmtCurrency(d.goal) : "—"}</div>
        <div className="text-sm text-slate-500">objetivo{pct != null && ` · ${pct}% feito`}</div>
        {pct != null && <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden" data-testid="goal-bar"><div className="h-full rounded-full bg-emerald-500" style={{ width: `${Math.min(100, pct)}%` }} /></div>}
      </div>
      <div>
        <div className="text-2xl font-bold text-emerald-600 tabular-nums" data-testid="upcoming">+ {fmtCurrency(d.upcoming || 0)}</div>
        <div className="text-sm text-slate-500" data-testid="upcoming-dates">a caminho · {n ? `${plural(n, "negócio", "negócios")} com proposta aceite ou CPCV` : "sem propostas aceites nem CPCV"}</div>
      </div>
    </div>
  );
}

export function NumbersCard({ d }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="rounded-[12px] border border-slate-100 bg-white" data-testid="numbers-card">
      <button onClick={() => setOpen(!open)} aria-expanded={open} className="w-full flex items-center justify-between px-3.5 py-3 text-[15px] text-slate-800" data-testid="numbers-toggle">
        Os meus números <ChevronDown size={16} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-3.5 pb-4"><NumbersBody d={d} /></div>}
    </section>
  );
}
